import { fetchUserAttributes } from "aws-amplify/auth";
import { uploadData } from "aws-amplify/storage";
import { message } from "antd";
import { isEntityId } from "common/businessAreaRoute";
import errorHandler from "common/errorHandler";
import { getArrayBufferFromFileObject } from "common/fileHandler";
import _ from "lodash";
import mime from "mime-types";
import parsePath from "parse-filepath";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import { v4 as uuidv4 } from "uuid";
import { selectSession } from "store/userLogin/session";
import * as actions from "./actions";

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;
const getUserBusinessAreasDict = (state) => state.home.businessAreasDict;

function* getUserBusinessAreaByName(api, tenantId, userId, businessAreaName) {
    let result = yield call([api, api.get], `${Endpoints.tenants}/api/v1/users/${userId}/businessareas/name/${businessAreaName}`, { tenantid: tenantId });
    return result.data;
}

function* getUserBusinessAreaList(api, tenantId, userId) {
    let result = yield call([api, api.get], `${Endpoints.tenants}/api/v1/users/${userId}/businessareas`, { tenantid: tenantId });
    return (result.data && result.data.Items) || [];
}

// The user's business area by its name; by its id when the "name" is an id one of theirs has (a
// link built from ids): the user's own list says which, then it is read by its name as usual.
function* getUserBusinessAreaByNameOrId(api, tenantId, userId, businessAreaName) {
    try {
        return yield call(getUserBusinessAreaByName, api, tenantId, userId, businessAreaName);
    } catch (error) {
        const notFound = error && error.isAxiosError && error.response && error.response.status === 404;
        if (!notFound || !isEntityId(businessAreaName)) {
            throw error;
        }
        const businessAreas = yield call(getUserBusinessAreaList, api, tenantId, userId);
        const byId = businessAreas.find((item) => item.businessAreaId && item.businessAreaId.toLowerCase() === businessAreaName.toLowerCase());
        if (!byId || !byId.name) {
            throw error;
        }
        return yield call(getUserBusinessAreaByName, api, tenantId, userId, byId.name);
    }
}

function* getSchemaModels(api, tenantId, businessAreaId, pageKey = null) {
    let params = {};
    if (pageKey) {
        params.pageKey = pageKey;
    }
    let result = yield call(
        [api, api.get],
        `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas`,
        { tenantid: tenantId },
        {
            params,
        },
    );
    return result.data;
}

function* getRecentSchemaModels(api, tenantId) {
    let result = yield call([api, api.get], `${Endpoints.schemas}/api/v1/schemas/recents`, { tenantid: tenantId });
    return result.data;
}

function* createSchemModel(api, tenantId, businessAreaId, schemaModel) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas`, schemaModel, { tenantid: tenantId });
    return result.data;
}

function* deleteSchemaModel(api, tenantId, businessAreaId, schemaId) {
    let result = yield call([api, api.delete], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}`, { tenantid: tenantId });
    return result.data;
}

function* getSchemaModel(api, tenantId, businessAreaId, schemaId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}`, { tenantid: tenantId });
        return result.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* getSchemaModelByName(api, tenantId, businessAreaId, schemaName) {
    try {
        let result = yield call([api, api.get], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/name/${schemaName}`, { tenantid: tenantId });
        return result.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateSchemaModel(api, tenantId, businessAreaId, schemaId, updateSchemaModelRequest) {
    let result = yield call([api, api.put], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}`, updateSchemaModelRequest, { tenantid: tenantId });
    return result.data;
}

export function* getUserSchemasModelPermissions(api, tenantId) {
    //TODo - remove the current user call and replace it with pretectum user
    let userAttributes = yield call(fetchUserAttributes);
    let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${userAttributes.sub}/permissions/resources/schemas`, { tenantid: tenantId });
    return result.data;
}

export function* getBusinessAreaDataForBusinessArea(api, tenantId, businessAreaId) {
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/datadomains`, { tenantid: tenantId });
    return result.data;
}

export function* getUserBusinessAreaByNameRequest(api, { businessAreaName }) {
    try {
        const { tenant, user } = yield call(selectSession);
        let userBusinessArea = yield call(getUserBusinessAreaByNameOrId, api, tenant.tenantId, user.userId, businessAreaName);
        userBusinessArea.user = { ...(userBusinessArea.user || {}), canAdd: false, canView: false, canEdit: false, canDelete: false };
        if (userBusinessArea.user && userBusinessArea.user.actions && userBusinessArea.user.actions.length > 0) {
            if (userBusinessArea.user.actions.includes("add")) {
                userBusinessArea.user.canAdd = true;
            }
            if (userBusinessArea.user.actions.includes("view")) {
                userBusinessArea.user.canView = true;
            }
            if (userBusinessArea.user.actions.includes("edit")) {
                userBusinessArea.user.canEdit = true;
            }
            if (userBusinessArea.user.actions.includes("delete")) {
                userBusinessArea.user.canDelete = true;
            }
        }
        yield put(actions.getUserBusinessAreaByNameSuccess(userBusinessArea, businessAreaName));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "user business area");
        yield put(actions.getUserBusinessAreaByNameFailure({ ...errorObject, businessAreaName }));
    }
}

export function* getSchemaModelsRequest(api, { businessAreaId }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getSchemaModels, api, tenant.tenantId, businessAreaId);
        yield put(actions.getSchemaModelsSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "schema models");
        yield put(actions.getSchemaModelsFailure(errorObject));
    }
}

export function* getSchemaModelsNextPageRequest(api, { businessAreaId, pageKey }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getSchemaModels, api, tenant.tenantId, businessAreaId, pageKey);
        yield put(actions.getSchemaModelsNextPageSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "schema models");
        yield put(actions.getSchemaModelsNextPageFailure(errorObject));
    }
}

export function* getRecentSchemaModelsRequest(api) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getRecentSchemaModels, api, tenant.tenantId);
        let userBusinessAreasDict = yield select(getUserBusinessAreasDict);
        if (result && result.Items && result.Items.length > 0) {
            for (let schemaModel of result.Items) {
                if (userBusinessAreasDict && userBusinessAreasDict[schemaModel.businessAreaId]) {
                    schemaModel.businessAreaName = userBusinessAreasDict[schemaModel.businessAreaId].name;
                }
            }
        }
        yield put(actions.getRecentSchemaModelsSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "recent schema models");
        yield put(actions.getRecentSchemaModelsFailure(errorObject));
    }
}

export function* createSchemModelRequest(api, { businessAreaId, schemaModel }) {
    try {
        let tenant = yield select(getTenant);
        let newSchemaModel = yield call(createSchemModel, api, tenant.tenantId, businessAreaId, schemaModel);
        yield put(actions.createSchemModelSuccess({ schemaModel: newSchemaModel }));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "schema model");
        yield put(actions.createSchemModelFailure(errorObject));
    }
}

export function* deleteSchemaModelRequest(api, { businessAreaId, schemaId }) {
    try {
        let tenant = yield select(getTenant);
        yield call(deleteSchemaModel, api, tenant.tenantId, businessAreaId, schemaId);
        yield call(getSchemaModelsRequest, api, { businessAreaId });
        yield put(actions.deleteSchemaModelSuccess(businessAreaId, schemaId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "schema models");
        yield put(actions.deleteSchemaModelFailure(errorObject));
    }
}

export function* getSchemaModelRequest(api, { businessAreaId, schemaId }) {
    try {
        let tenant = yield select(getTenant);
        let schemaModel = yield call(getSchemaModel, api, tenant.tenantId, businessAreaId, schemaId);
        yield put(actions.getSchemaModelSuccess(schemaModel));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "schema model");
        yield put(actions.getSchemaModelFailure(errorObject));
    }
}

export function* getSchemaModelByNameRequest(api, { businessAreaId, schemaName }) {
    try {
        const { tenant } = yield call(selectSession);
        let schemaModel = yield call(getSchemaModelByName, api, tenant.tenantId, businessAreaId, schemaName);
        // A link built from ids names the schema by its id.
        if (!schemaModel && isEntityId(schemaName)) {
            schemaModel = yield call(getSchemaModel, api, tenant.tenantId, businessAreaId, schemaName);
        }
        yield put(actions.getSchemaModelSuccess(schemaModel));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "schema model by name");
        yield put(actions.getSchemaModelFailure(errorObject));
    }
}

export function* updateSchemaModelRequest(api, { businessAreaId, schemaId, updateSchemaModelData, fieldIndexMappings, source }) {
    let { newFieldIndexMappings, updatedSchemaFieldIndexMapping } = fieldIndexMappings;
    try {
        let tenant = yield select(getTenant);
        let updateSchemaModelRequest = {};
        if (updateSchemaModelData.schemaModel) {
            updateSchemaModelRequest.schemaModel = {
                updateRequest: updateSchemaModelData.schemaModel,
            };
        }
        if (updateSchemaModelData.newFields.length > 0 || updateSchemaModelData.deletedFields.length > 0 || updateSchemaModelData.updatedFields.length > 0) {
            updateSchemaModelRequest.fields = {};
            if (updateSchemaModelData.newFields.length > 0) {
                updateSchemaModelRequest.fields.insertRequest = updateSchemaModelData.newFields;
            }
            if (updateSchemaModelData.deletedFields.length > 0) {
                updateSchemaModelRequest.fields.deleteRequest = updateSchemaModelData.deletedFields;
            }
            if (updateSchemaModelData.updatedFields.length > 0) {
                updateSchemaModelRequest.fields.updateRequest = updateSchemaModelData.updatedFields;
            }
        }
        if (_.isEmpty(updateSchemaModelRequest) === false) {
            yield call(updateSchemaModel, api, tenant.tenantId, businessAreaId, schemaId, updateSchemaModelRequest);
            yield call(getSchemaModelRequest, api, { businessAreaId, schemaId });
        }
        yield put(actions.updateSchemaModelSuccess(source));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "schema model");
        if (error.isAxiosError) {
            if ([400, 404, 409, 412].includes(error.response.status) === false) {
                yield call(getSchemaModelRequest, api, { businessAreaId, schemaId });
            }
        }
        if (errorObject.fields) {
            let fields = {};
            for (let fieldKey in errorObject.fields) {
                let fieldPaths = fieldKey.split(".");
                if (fieldPaths[0] === "fields") {
                    if (fieldPaths[1] === "updateRequest") {
                        fieldPaths[2] = updatedSchemaFieldIndexMapping[parseInt(fieldPaths[2])];
                        fields[["fields", fieldPaths[2], fieldPaths[3]].join(".")] = errorObject.fields[fieldKey];
                    } else if (fieldPaths[1] === "insertRequest") {
                        fieldPaths[2] = newFieldIndexMappings[parseInt(fieldPaths[2])];
                        fields[["fields", fieldPaths[2], fieldPaths[3]].join(".")] = errorObject.fields[fieldKey];
                    } else {
                        fields[fieldKey] = errorObject.fields[fieldKey];
                    }
                } else {
                    fields[fieldKey] = errorObject.fields[fieldKey];
                }
            }
            errorObject.fields = fields;
        }
        yield put(actions.updateSchemaModelFailure(errorObject));
    }
}

export function* getUserSchemaModelsPermissionRequest(api) {
    try {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        let tenant = yield select(getTenant);
        let permissionResponse = yield call(getUserSchemasModelPermissions, api, tenant.tenantId);
        if (permissionResponse && permissionResponse.Items && permissionResponse.Items.length > 0) {
            let permissionActions = permissionResponse.Items.map((item) => item.actions);
            permissionActions = _.union(...permissionActions);
            if (permissionActions.length > 0) {
                if (permissionActions.includes("add")) {
                    permission.canAdd = true;
                }
                if (permissionActions.includes("view")) {
                    permission.canView = true;
                }
                if (permissionActions.includes("edit")) {
                    permission.canEdit = true;
                }
                if (permissionActions.includes("delete")) {
                    permission.canDelete = true;
                }
            }
        }

        yield put(actions.getUserSchemaModelsPermissionSuccess(permission));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "user schema model permissions");
        yield put(actions.getUserSchemaModelsPermissionFailure(errorObject));
    }
}

export function* getBusinessAreaDataForBusinessAreaRequest(api, { businessAreaId }) {
    try {
        let tenant = yield select(getTenant);
        let items = yield call(getBusinessAreaDataForBusinessArea, api, tenant.tenantId, businessAreaId);
        yield put(actions.getBusinessAreaDataForBusinessAreaSuccess(items));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "business area data for business area");
        yield put(actions.getBusinessAreaDataForBusinessAreaFailure(errorObject));
    }
}

export function* uploadSchemaModelDefinitionRequest(api, { businessAreaId, dataFile }) {
    try {
        let tenant = yield select(getTenant);
        let user = yield select(getUser);
        let fileName = `tenants/${tenant.tenantId}/users/${user.userId}/schemadefinitions/${uuidv4()}${parsePath(dataFile.name).ext}`;
        let arrayBuffer = yield call(getArrayBufferFromFileObject, dataFile);
        let uploadTask = uploadData({
            path: `public/${fileName}`,
            data: arrayBuffer,
            options: {
                contentType: dataFile.type || mime.contentType(dataFile.name) || "",
            },
        });
        let result = yield call(() => uploadTask.result);
        yield put(
            actions.uploadSchemaModelDefinitionSuccess({
                bucket: process.env.REACT_APP_BUCKET_NAME,
                key: result.path,
                region: process.env.REACT_APP_BUCKET_REGION,
            }),
        );
    } catch (error) {
        let errorObject = errorHandler(error, "Upload", "schema definition file");
        yield put(actions.uploadSchemaModelDefinitionFailure(errorObject));
    }
}

export function* watchGetUserBusinessAreaByNameRequest(api, { params }) {
    yield call(getUserBusinessAreaByNameRequest, api, params);
}

export function* watchGetSchemaModelsRequest(api, { params }) {
    yield call(getSchemaModelsRequest, api, params);
}

export function* watchGetSchemaModelsNextPageRequest(api, { params }) {
    yield call(getSchemaModelsNextPageRequest, api, params);
}

export function* watchGetRecentSchemaModelsRequest(api, params) {
    yield call(getRecentSchemaModelsRequest, api);
}

export function* watchCreateSchemModelRequest(api, { params }) {
    yield call(createSchemModelRequest, api, params);
}

export function* watchdeleteSchemaModelRequest(api, { params }) {
    yield call(deleteSchemaModelRequest, api, params);
}

export function* watchGetSchemaModelRequest(api, { params }) {
    yield call(getSchemaModelRequest, api, params);
}

export function* watchGetSchemaModelByNameRequest(api, { params }) {
    yield call(getSchemaModelByNameRequest, api, params);
}

export function* watchUpdateSchemaModelRequest(api, { params }) {
    yield call(updateSchemaModelRequest, api, params);
}

export function* watchGetUserSchemaModelsPermissionRequest(api, { params }) {
    yield call(getUserSchemaModelsPermissionRequest, api);
}

export function* watchGetBusinessAreaDataForBusinessAreaRequest(api, { params }) {
    yield call(getBusinessAreaDataForBusinessAreaRequest, api, params);
}

export function* watchUploadSchemaModelDefinitionRequest(api, { params }) {
    yield call(uploadSchemaModelDefinitionRequest, api, params);
}

function* getMappingConfigurations(api, tenantId, businessAreaId, schemaId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/mapping-configurations`);
        return result.data;
    } catch (error) {
        if (error.isAxiosError && error.response && error.response.status === 404) {
            let schemaModel = yield call(getSchemaModel, api, tenantId, businessAreaId, schemaId);
            return schemaModel?.mappingConfigurations || [];
        }
        throw error;
    }
}

function* saveMappingConfiguration(api, tenantId, businessAreaId, schemaId, title, mappingConfiguration) {
    let existingConfigs = [];
    try {
        let result = yield call(getMappingConfigurations, api, tenantId, businessAreaId, schemaId);
        existingConfigs = result?.Items || result || [];
    } catch (error) {
        existingConfigs = [];
    }

    const newConfig = {
        configurationId: `config_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: title,
        mappingConfiguration: mappingConfiguration,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };

    existingConfigs.push(newConfig);

    try {
        let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/mapping-configurations`, {
            title,
            mappingConfiguration,
        });
        return result.data;
    } catch (error) {
        if (error.isAxiosError && (error.response?.status === 404 || error.response?.status === 405)) {
            let updateRequest = {
                schemaModel: {
                    updateRequest: {
                        mappingConfigurations: existingConfigs,
                    },
                },
            };
            yield call(updateSchemaModel, api, tenantId, businessAreaId, schemaId, updateRequest);
            return { Items: existingConfigs };
        }
        throw error;
    }
}

function* deleteMappingConfiguration(api, tenantId, businessAreaId, schemaId, configurationId) {
    let existingConfigs = [];
    try {
        let result = yield call(getMappingConfigurations, api, tenantId, businessAreaId, schemaId);
        existingConfigs = result?.Items || result || [];
    } catch (error) {
        existingConfigs = [];
    }

    existingConfigs = existingConfigs.filter((config) => config.configurationId !== configurationId);

    try {
        let result = yield call(
            [api, api.delete],
            `${Endpoints.schemas}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/mapping-configurations/${configurationId}`,
            null,
        );
        return result.data;
    } catch (error) {
        if (error.isAxiosError && (error.response?.status === 404 || error.response?.status === 405)) {
            let updateRequest = {
                schemaModel: {
                    updateRequest: {
                        mappingConfigurations: existingConfigs,
                    },
                },
            };
            yield call(updateSchemaModel, api, tenantId, businessAreaId, schemaId, updateRequest);
            return { Items: existingConfigs };
        }
        throw error;
    }
}

export function* saveMappingConfigurationRequest(api, { businessAreaId, schemaId, title, mappingConfiguration }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(saveMappingConfiguration, api, tenant.tenantId, businessAreaId, schemaId, title, mappingConfiguration);
        yield put(actions.saveMappingConfigurationSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Save", "mapping configuration");
        yield put(actions.saveMappingConfigurationFailure(errorObject));
    }
}

export function* getMappingConfigurationsRequest(api, { businessAreaId, schemaId }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getMappingConfigurations, api, tenant.tenantId, businessAreaId, schemaId);
        yield put(actions.getMappingConfigurationsSuccess({ Items: Array.isArray(result) ? result : result?.Items || [] }));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "mapping configurations");
        yield put(actions.getMappingConfigurationsSuccess({ Items: [] }));
    }
}

export function* deleteMappingConfigurationRequest(api, { businessAreaId, schemaId, configurationId }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(deleteMappingConfiguration, api, tenant.tenantId, businessAreaId, schemaId, configurationId);
        yield put(actions.deleteMappingConfigurationSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "mapping configuration");
        yield put(actions.deleteMappingConfigurationFailure(errorObject));
    }
}

export function* watchSaveMappingConfigurationRequest(api, { params }) {
    yield call(saveMappingConfigurationRequest, api, params);
}

export function* watchGetMappingConfigurationsRequest(api, { params }) {
    yield call(getMappingConfigurationsRequest, api, params);
}

export function* watchDeleteMappingConfigurationRequest(api, { params }) {
    yield call(deleteMappingConfigurationRequest, api, params);
}

function* generateSchemaHealthReport(api, schemaId, businessAreaId, regenerate) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/schema/health-report`, {
        schemaId,
        businessAreaId,
        regenerate,
    });
    return result.data;
}

function* getAiJobStatus(api, jobId) {
    let result = yield call([api, api.get], `${Endpoints.schemas}/api/v1/ai/jobs/${jobId}`);
    return result.data;
}

function* waitForAiJobCompletion(api, jobId, featureLabel) {
    const maxPollCount = 40;
    const pollIntervalMs = 3000;

    for (let pollCount = 0; pollCount < maxPollCount; pollCount++) {
        let statusResult = null;
        try {
            statusResult = yield call(getAiJobStatus, api, jobId);
        } catch (error) {
            if (!(error?.isAxiosError && error?.response?.status === 404)) {
                throw error;
            }
        }

        if (statusResult?.status === "COMPLETED") {
            return statusResult;
        }

        if (statusResult?.status === "FAILED") {
            throw new Error(statusResult?.errorMessage || `${featureLabel} generation failed.`);
        }

        yield delay(pollIntervalMs);
    }

    throw new Error(`${featureLabel} generation is taking longer than expected. Please check again in a moment.`);
}

function getAiWarningMessage(statusResult) {
    return (
        statusResult?.result?.warningMessage ||
        statusResult?.computed?.warningMessage ||
        null
    );
}

export function* watchGenerateSchemaHealthReportRequest(api, { params }) {
    try {
        const jobResult = yield call(generateSchemaHealthReport, api, params.schemaId, params.businessAreaId, params.regenerate);
        const completedResult = yield call(waitForAiJobCompletion, api, jobResult.jobId, "Schema health report");
        const warningMessage = getAiWarningMessage(completedResult);
        if (warningMessage) {
            message.warning(warningMessage);
        }
        yield put(actions.generateSchemaHealthReportSuccess(completedResult.reportHtml));
    } catch (error) {
        let errorObject = errorHandler(error, "Generate", "schema health report");
        yield put(actions.generateSchemaHealthReportFailure(errorObject));
    }
}

function* getPiiSuggestions(api, schemaId, businessAreaId) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/schema/pii-suggestions`, {
        schemaId,
        businessAreaId,
    });
    return result.data;
}

export function* watchGetPiiSuggestionsRequest(api, { params }) {
    try {
        const jobResult = yield call(getPiiSuggestions, api, params.schemaId, params.businessAreaId);
        const completedResult = yield call(waitForAiJobCompletion, api, jobResult.jobId, "PII suggestions");
        const warningMessage = getAiWarningMessage(completedResult);
        if (warningMessage) {
            message.warning(warningMessage);
        }
        yield put(actions.getPiiSuggestionsSuccess(completedResult.result || completedResult.computed || null));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "PII suggestions");
        if (error.isAxiosError && error.response?.status === 500 && error.response?.data?.message?.includes("quota exceeded")) {
            errorObject.message = "OpenAI API quota exceeded. Please check your plan and billing details, or try again later.";
        }
        yield put(actions.getPiiSuggestionsFailure(errorObject));
    }
}

export function* watchSavePiiSuggestionsRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let { businessAreaId, schemaId, updatedFields } = params;
        let updateRequest = {
            fields: {
                updateRequest: updatedFields,
            },
        };
        yield call(updateSchemaModel, api, tenant.tenantId, businessAreaId, schemaId, updateRequest);
        yield call(getSchemaModelRequest, api, { businessAreaId, schemaId });
        yield put(actions.savePiiSuggestionsSuccess());
    } catch (error) {
        let errorObject = errorHandler(error, "Save", "PII suggestions");
        yield put(actions.savePiiSuggestionsFailure(errorObject));
    }
}

function* getFieldDescriptions(api, schemaId, businessAreaId) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/schema/field-descriptions`, {
        schemaId,
        businessAreaId,
    });
    return result.data;
}

export function* watchGetFieldDescriptionsRequest(api, { params }) {
    try {
        const jobResult = yield call(getFieldDescriptions, api, params.schemaId, params.businessAreaId);
        const completedResult = yield call(waitForAiJobCompletion, api, jobResult.jobId, "Field descriptions");
        const warningMessage = getAiWarningMessage(completedResult);
        if (warningMessage) {
            message.warning(warningMessage);
        }
        yield put(actions.getFieldDescriptionsSuccess(completedResult.result || completedResult.computed || null));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "field descriptions");
        if (error.isAxiosError && error.response?.status === 500 && error.response?.data?.message?.includes("quota exceeded")) {
            errorObject.message = "OpenAI API quota exceeded. Please check your plan and billing details, or try again later.";
        }
        yield put(actions.getFieldDescriptionsFailure(errorObject));
    }
}

export function* watchSaveFieldDescriptionsRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let { businessAreaId, schemaId, updatedFields, schemaDescription } = params;
        let updateRequest = {
            fields: {
                updateRequest: updatedFields,
            },
        };
        if (schemaDescription) {
            updateRequest.description = schemaDescription;
        }
        yield call(updateSchemaModel, api, tenant.tenantId, businessAreaId, schemaId, updateRequest);
        yield call(getSchemaModelRequest, api, { businessAreaId, schemaId });
        yield put(actions.saveFieldDescriptionsSuccess());
    } catch (error) {
        let errorObject = errorHandler(error, "Save", "field descriptions");
        yield put(actions.saveFieldDescriptionsFailure(errorObject));
    }
}

function* generateDdlSql(api, schemaId, businessAreaId) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/schema/ddl-sql`, {
        schemaId,
        businessAreaId,
    });
    return result.data;
}

export function* watchGenerateDdlSqlRequest(api, { params }) {
    try {
        let result = yield call(generateDdlSql, api, params.schemaId, params.businessAreaId);
        yield put(actions.generateDdlSqlSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Generate", "DDL SQL");
        if (error.isAxiosError && error.response?.status === 429) {
            errorObject.message = "OpenAI API quota exceeded. Please check your plan and billing details, or try again later.";
        }
        yield put(actions.generateDdlSqlFailure(errorObject));
    }
}

function* generateGapPrediction(api, schemaId, businessAreaId, regenerate) {
    let result = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/schema/gap-prediction`, {
        schemaId,
        businessAreaId,
        regenerate,
    });
    return result.data;
}

export function* watchGenerateGapPredictionRequest(api, { params }) {
    try {
        const jobResult = yield call(generateGapPrediction, api, params.schemaId, params.businessAreaId, params.regenerate === true);
        const completedResult = yield call(waitForAiJobCompletion, api, jobResult.jobId, "Gap prediction");
        const warningMessage = getAiWarningMessage(completedResult);
        if (warningMessage) {
            message.warning(warningMessage);
        }
        const gapPredictionPayload = completedResult.result || completedResult.computed || {
            schemaId: completedResult.schemaId,
            businessAreaId: completedResult.businessAreaId,
            predictionHtml: completedResult.reportHtml || null,
        };
        if (!gapPredictionPayload.predictionHtml && completedResult.reportHtml) {
            gapPredictionPayload.predictionHtml = completedResult.reportHtml;
        }
        yield put(actions.generateGapPredictionSuccess(gapPredictionPayload));
    } catch (error) {
        let errorObject = errorHandler(error, "Generate", "Gap Prediction");
        if (error.isAxiosError && error.response?.status === 429) {
            errorObject.message = "OpenAI API quota exceeded. Please check your plan and billing details, or try again later.";
        }
        yield put(actions.generateGapPredictionFailure(errorObject));
    }
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_USER_BUSINESS_AREA_BY_NAME_REQUEST, watchGetUserBusinessAreaByNameRequest, api);
    yield takeLatest(actions.GET_SCHEMA_MODELS_REQUEST, watchGetSchemaModelsRequest, api);
    yield takeLatest(actions.GET_SCHEMA_MODELS_NEXT_PAGE_REQUEST, watchGetSchemaModelsNextPageRequest, api);
    yield takeLatest(actions.GET_RECENT_SCHEMA_MODELS_REQUEST, watchGetRecentSchemaModelsRequest, api);
    yield takeLatest(actions.CREATE_SCHEMA_MODEL_REQUEST, watchCreateSchemModelRequest, api);
    yield takeLatest(actions.DELETE_SCHEMA_MODEL_REQUEST, watchdeleteSchemaModelRequest, api);
    yield takeLatest(actions.GET_SCHEMA_MODEL_REQUEST, watchGetSchemaModelRequest, api);
    yield takeLatest(actions.GET_SCHEMA_MODEL_BY_NAME_REQUEST, watchGetSchemaModelByNameRequest, api);
    yield takeLatest(actions.UPDATE_SCHEMA_MODEL_REQUEST, watchUpdateSchemaModelRequest, api);
    yield takeLatest(actions.GET_USER_SCHEMA_MODELS_PERMISSION_REQUEST, watchGetUserSchemaModelsPermissionRequest, api);
    yield takeLatest(actions.GET_BUSINESS_AREA_DATA_FOR_BUSINESS_AREA_REQUEST, watchGetBusinessAreaDataForBusinessAreaRequest, api);
    yield takeLatest(actions.UPLOAD_SCHEMA_MODEL_DEFINITION_REQUEST, watchUploadSchemaModelDefinitionRequest, api);
    yield takeLatest(actions.SAVE_MAPPING_CONFIGURATION_REQUEST, watchSaveMappingConfigurationRequest, api);
    yield takeLatest(actions.GET_MAPPING_CONFIGURATIONS_REQUEST, watchGetMappingConfigurationsRequest, api);
    yield takeLatest(actions.DELETE_MAPPING_CONFIGURATION_REQUEST, watchDeleteMappingConfigurationRequest, api);
    yield takeLatest(actions.GENERATE_SCHEMA_HEALTH_REPORT_REQUEST, watchGenerateSchemaHealthReportRequest, api);
    yield takeLatest(actions.GET_PII_SUGGESTIONS_REQUEST, watchGetPiiSuggestionsRequest, api);
    yield takeLatest(actions.SAVE_PII_SUGGESTIONS_REQUEST, watchSavePiiSuggestionsRequest, api);
    yield takeLatest(actions.GET_FIELD_DESCRIPTIONS_REQUEST, watchGetFieldDescriptionsRequest, api);
    yield takeLatest(actions.SAVE_FIELD_DESCRIPTIONS_REQUEST, watchSaveFieldDescriptionsRequest, api);
    yield takeLatest(actions.GENERATE_DDL_SQL_REQUEST, watchGenerateDdlSqlRequest, api);
    yield takeLatest(actions.GENERATE_GAP_PREDICTION_REQUEST, watchGenerateGapPredictionRequest, api);
}
